import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { useWindowVirtualizer } from '@tanstack/react-virtual'
import type { FeedItem as FeedItemT, FeedItemOf } from '@algoboo/schema/api'
import type { PerfRange } from '@algoboo/schema'
import { Avatar, Button, EmptyState, ErrorState, FeedItem, Skeleton, Sparkline, cx, type AvatarProps } from '@algoboo/ui'
import { errorCopy, invalidate, isApiError, useAgentPerformance, useApiMutation, useKnownAvatar } from '../lib/api'
import { perfSeries } from '../lib/perf'
import { useOpenEvidence } from './evidence'
import { useRequireSession } from './session'
import { PostActions } from './social/Reactions'
import { useNow } from '../lib/now'

/** Avatar that uses the agent's configured mascot when the payload lacks it. */
export function AgentAvatar(props: AvatarProps) {
  const known = useKnownAvatar(props.handle)
  return <Avatar {...props} avatar={props.avatar ?? known ?? null} />
}

export function PerfSpark({ handle, range = '7d', width = 120, height = 30, variant = 'inline', label = 'Equity' }: { handle: string; range?: PerfRange; width?: number; height?: number; variant?: 'inline' | 'chart'; label?: string }) {
  const q = useAgentPerformance(handle, range)
  if (q.isLoading) return <Skeleton style={{ width: variant === 'chart' ? '100%' : width, height }} />
  const pts = perfSeries(q.data) ?? []
  return <Sparkline points={pts} label={label} width={width} height={height} variant={variant} />
}

function PerfItemSeries({ item }: { item: FeedItemOf<'performance'> }) {
  return <PerfSpark handle={item.agent.handle} width={112} height={28} />
}
const renderSeries = (item: FeedItemOf<'performance'>) => <PerfItemSeries item={item} />

function FeedRow({ item, fresh, now }: { item: FeedItemT; fresh: boolean; now: number }) {
  const open = useOpenEvidence()
  const known = useKnownAvatar(item.agent.handle)
  return <FeedItem item={item} avatar={item.agent.avatar ?? known ?? null} fresh={fresh} onEvidence={open} renderSeries={renderSeries} now={now} actions={<PostActions item={item} />} />
}

const VIRTUALIZE_AFTER = 200

/**
 * The feed list: plain rendering for short lists, window virtualization
 * beyond 200 items, infinite loading with an intersection sentinel.
 */
export function FeedList({
  items,
  fresh,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
  isLoading,
  error,
  refetch,
  empty,
  className,
}: {
  items: readonly FeedItemT[]
  fresh?: ReadonlySet<string>
  hasNextPage: boolean
  fetchNextPage: () => void
  isFetchingNextPage: boolean
  isLoading: boolean
  error: unknown
  refetch: () => void
  empty?: ReactNode
  className?: string
}) {
  const sentinel = useRef<HTMLDivElement>(null)
  const now = useNow(30_000)
  useEffect(() => {
    const el = sentinel.current
    if (!el || !hasNextPage) return
    const io = new IntersectionObserver((es) => {
      if (es.some((e) => e.isIntersecting) && !isFetchingNextPage) fetchNextPage()
    }, { rootMargin: '600px' })
    io.observe(el)
    return () => io.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  if (isLoading)
    return (
      <div className={cx('divide-y divide-line', className)} aria-busy="true" aria-label="Loading feed">
        {Array.from({ length: 6 }, (_, i) => (
          <div key={i} className="flex gap-3 px-5 py-4">
            <Skeleton className="h-10 w-10 rounded-[32%]" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-1/3" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          </div>
        ))}
      </div>
    )
  if (error && items.length === 0) {
    const c = isApiError(error) ? errorCopy(error.code) : { title: 'The feed did not load', body: 'Check your connection and try again.' }
    return <ErrorState title={c.title} body={c.body} onRetry={refetch} />
  }
  if (items.length === 0) return <>{empty ?? <EmptyState title="Quiet on the floor" body="No posts match these filters yet." />}</>

  return (
    <div className={className}>
      {items.length > VIRTUALIZE_AFTER ? <VirtualRows items={items} fresh={fresh} now={now} /> : (
        <ol className="divide-y divide-line" aria-label="Posts">
          {items.map((it) => (
            <li key={it.id}>
              <FeedRow item={it} fresh={fresh?.has(it.id) ?? false} now={now} />
            </li>
          ))}
        </ol>
      )}
      <div ref={sentinel} className="flex justify-center border-t border-line py-4">
        {hasNextPage ? (
          <Button size="sm" variant="ghost" loading={isFetchingNextPage} onClick={() => fetchNextPage()}>
            Load older posts
          </Button>
        ) : (
          <span className="text-[11px] font-medium uppercase tracking-[0.06em] text-ink-3">you reached the first post</span>
        )}
      </div>
    </div>
  )
}

function VirtualRows({ items, fresh, now }: { items: readonly FeedItemT[]; fresh: ReadonlySet<string> | undefined; now: number }) {
  const listRef = useRef<HTMLOListElement>(null)
  // the list's offset in the page, measured after layout (render never reads the ref)
  const [scrollMargin, setScrollMargin] = useState(0)
  useLayoutEffect(() => {
    const top = listRef.current?.offsetTop ?? 0
    if (top !== scrollMargin) setScrollMargin(top)
  }, [items, scrollMargin])
  const v = useWindowVirtualizer({ count: items.length, estimateSize: () => 150, overscan: 8, scrollMargin })
  return (
    <ol ref={listRef} aria-label="Posts" style={{ height: v.getTotalSize(), position: 'relative' }}>
      {v.getVirtualItems().map((row) => {
        const it = items[row.index]!
        return (
          <li key={it.id} data-index={row.index} ref={v.measureElement} className="absolute left-0 right-0 border-b border-line" style={{ transform: `translateY(${row.start - v.options.scrollMargin}px)` }}>
            <FeedRow item={it} fresh={fresh?.has(it.id) ?? false} now={now} />
          </li>
        )
      })}
    </ol>
  )
}

export function FollowButton({ handle, following, size = 'md' }: { handle: string; following: boolean; size?: 'sm' | 'md' }) {
  const require = useRequireSession()
  const follow = useApiMutation('followAgent', { invalidate: () => invalidate.follow(handle) })
  const unfollow = useApiMutation('unfollowAgent', { invalidate: () => invalidate.follow(handle) })
  const busy = follow.isPending || unfollow.isPending
  return (
    <Button
      size={size}
      variant={following ? 'secondary' : 'primary'}
      icon={following ? 'check' : 'plus'}
      loading={busy}
      aria-pressed={following}
      onClick={() => require(`Sign in to follow @${handle}.`, () => (following ? unfollow.mutate({ params: { handle } }) : follow.mutate({ params: { handle } })))}
    >
      {following ? 'Following' : 'Follow'}
    </Button>
  )
}

/** Inline error for a section whose query failed. */
export function QueryError({ error, refetch, compact = true }: { error: unknown; refetch?: () => void; compact?: boolean }) {
  const c = isApiError(error) ? errorCopy(error.code) : { title: 'Could not load this', body: 'Check your connection and try again.' }
  return <ErrorState compact={compact} title={c.title} body={c.body} onRetry={refetch} />
}

/** A view name the page does not have (lane BOOCLEAN1): it answers as unknown, never as a redirect to another view. */
export function NoSuchView({ name, page }: { name: string; page: string }) {
  return (
    <div data-testid="no-such-view" data-view={name}>
      <EmptyState title={`No view called “${name}”`} body={`${page} has no view by that name. The address may be an old one: nothing redirects.`} />
    </div>
  )
}
